import React, { useState, useCallback } from 'react';
import { useDropzone, FileWithPath } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { UploadCloud, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface MediaUploadProps {
  onFilesChange: (files: FileWithPath[]) => void; // New files selected
  onInitialFileRemoved?: (removedUrl: string) => void; // Callback when an initial file is marked for removal
  initialFiles?: { url: string; name: string }[]; 
  maxFiles?: number;
  accept?: Record<string, string[]>;
}

const MediaUpload: React.FC<MediaUploadProps> = ({ 
    onFilesChange, 
    onInitialFileRemoved, // New prop
    initialFiles = [], 
    maxFiles = 5, 
    accept = { 'image/*': ['.png', '.jpeg', '.jpg'] } 
}) => {
  const { t } = useTranslation(['forms', 'common']);
  const [newFiles, setNewFiles] = useState<FileWithPath[]>([]);
  // Use initialFiles directly for display, removal is handled via callback
  // const [keptInitialFiles, setKeptInitialFiles] = useState<{ url: string; name: string }[]>(initialFiles);

  const onDrop = useCallback((acceptedFiles: FileWithPath[]) => {
    const currentTotalFiles = initialFiles.length + newFiles.length;
    const availableSlots = maxFiles - currentTotalFiles;
    const filesToAdd = acceptedFiles.slice(0, Math.max(0, availableSlots));

    const combined = [...newFiles, ...filesToAdd];
    const uniqueFiles = Array.from(new Set(combined.map(f => f.name))).map(name => {
        return combined.find(f => f.name === name)!
    });
    // Ensure total doesn't exceed maxFiles (considering initial files shown)
    const finalNewFiles = uniqueFiles.slice(0, Math.max(0, maxFiles - initialFiles.length));
    setNewFiles(finalNewFiles);
    onFilesChange(finalNewFiles); 
  }, [newFiles, maxFiles, initialFiles.length, onFilesChange]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ 
      onDrop, 
      accept,
      // Calculate maxFiles dynamically based on initial files
      maxFiles: maxFiles - initialFiles.length > 0 ? maxFiles - initialFiles.length : 0,
      disabled: initialFiles.length >= maxFiles
    });

  const removeNewFile = (fileName: string) => {
    const updatedFiles = newFiles.filter(file => file.name !== fileName);
    setNewFiles(updatedFiles);
    onFilesChange(updatedFiles);
  };

  // Use the callback for initial file removal
  const handleInitialFileRemoveClick = (fileUrl: string) => {
      if (onInitialFileRemoved) {
          onInitialFileRemoved(fileUrl); 
          // Note: We don't remove it from initialFiles here, 
          // the parent component manages the deletion list and re-renders
          // with updated initialFiles if needed (though typically not needed until save)
      }
  };

  // Simple preview (can be enhanced)
  const renderPreview = (file: FileWithPath | { url: string; name: string }) => {
    const isNew = file instanceof File;
    const url = isNew ? URL.createObjectURL(file) : file.url;
    const name = file.name;
    const isImage = isNew ? file.type.startsWith('image/') : name.match(/\.(jpeg|jpg|png|gif)$/i);

    return (
      <div key={name} className="relative border rounded-md p-1 w-24 h-24 flex items-center justify-center overflow-hidden">
        {isImage ? (
          <img src={url} alt={name} className="object-cover w-full h-full" onLoad={() => { if (isNew) URL.revokeObjectURL(url); }} />
        ) : (
          <span className="text-xs text-center break-words">{name}</span>
        )}
        <Button 
          variant="destructive"
          size="sm"
          className="absolute top-0 right-0 p-1 h-5 w-5 rounded-full z-10"
          // Call the correct removal handler
          onClick={() => isNew ? removeNewFile(name) : handleInitialFileRemoveClick(file.url) }
        >
          <X className="h-3 w-3"/>
        </Button>
      </div>
    );
  };

  return (
    <div>
      <div 
        {...getRootProps()} 
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors 
                    ${isDragActive ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'}
                    {/* Calculate disabled state based on initial files length + new files length */}
                    ${(initialFiles.length + newFiles.length) >= maxFiles ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <input {...getInputProps()} />
        <UploadCloud className="mx-auto h-10 w-10 text-muted-foreground mb-2" />
        {/* Adjust count message */}
        <p>{isDragActive ? t('forms:dropzone_active') : t('forms:dropzone_inactive', { count: Math.max(0, maxFiles - (initialFiles.length + newFiles.length)) })}</p>
        <p className="text-xs text-muted-foreground mt-1">{Object.values(accept).flat().join(', ')}</p>
      </div>

      {(initialFiles.length > 0 || newFiles.length > 0) && (
        <div className="mt-4 flex flex-wrap gap-2">
          {initialFiles.map(file => renderPreview(file))}
          {newFiles.map(file => renderPreview(file))}
        </div>
      )}
    </div>
  );
};

export default MediaUpload; 